/**
 * Local, device-scoped UI state.
 *
 * Only things that are genuinely about *this browser* belong here. Anything the
 * user would expect to follow them to another device is server state:
 *
 *  - notification preferences → `/accounts/users/{id}/notification_settings/`
 *  - currency and profile fields → `User.preferences` via `authApi.updateMe`
 *  - saved filter presets → `/filters/`
 *
 * This store previously also carried `notifications` and `display` slices
 * (currency, dateFormat, numberFormat, timezone, compactMode,
 * showTrendIndicators). Nothing read them — `SettingsPage` had moved to the
 * API — so they were a second, silently diverging copy of values the server
 * owned. A stale local `currency` that no screen consults is worse than no
 * local currency at all, because the next reader cannot tell which one is real.
 */
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface SettingsState {
  sidebarCollapsed: boolean;

  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  resetSettings: () => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      sidebarCollapsed: false,

      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setSidebarCollapsed: (collapsed: boolean) => {
        set({ sidebarCollapsed: collapsed });
      },

      resetSettings: () => {
        set({ sidebarCollapsed: false });
      },
    }),
    {
      name: "everythingcloud-settings",
      // Bumped when the notification/display slices were removed. Without it,
      // a returning browser rehydrates the old blob and quietly carries the
      // dead keys forward forever.
      version: 1,
      migrate: (persisted) => ({
        sidebarCollapsed:
          (persisted as { sidebarCollapsed?: boolean } | null)
            ?.sidebarCollapsed ?? false,
      }),
      // Persist only what this store still owns, so a future addition has to be
      // an explicit decision rather than an accident of shape.
      partialize: (state) => ({ sidebarCollapsed: state.sidebarCollapsed }),
    },
  ),
);
